import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { storage } from '../utils/storage';
import { websocketService } from '../api/services/websocketService';

interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  token: string | null;
}

interface AuthContextValue extends AuthState {
  updateAuth: (token: string, user: any) => void;
  clearAuth: () => void;
  clearAllStorage: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const token = storage.getAuthToken();
    const user = storage.getUserData();
    return token && user
      ? { isAuthenticated: true, user, token }
      : { isAuthenticated: false, user: null, token: null };
  });

  useEffect(() => {
    if (authState.isAuthenticated) {
      try {
        websocketService.connect();
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
      }
    } else {
      websocketService.disconnect();
    }
  }, [authState.isAuthenticated]);

  const updateAuth = (token: string, user: any) => {
    storage.setAuthToken(token);
    storage.setUserData(user);

    if (user?.dealerPaymentStatus) {
      storage.setDealerPaymentStatus(user.dealerPaymentStatus);
    } else {
      storage.removeDealerPaymentStatus();
    }

    setAuthState({ isAuthenticated: true, user, token });
  };

  const clearAuth = () => {
    storage.clearAuth();
    setAuthState({ isAuthenticated: false, user: null, token: null });
  };

  const clearAllStorage = () => {
    storage.clearAllStorage();
    setAuthState({ isAuthenticated: false, user: null, token: null });
  };

  return (
    <AuthContext.Provider value={{ ...authState, updateAuth, clearAuth, clearAllStorage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider');
  return ctx;
};
